import React, { useEffect, useRef, useState } from "react"
import LazyLoad from "react-lazy-load"
import { useDispatch, useSelector } from "react-redux"
import cx from "classnames"
import _get from "lodash/get"
import _isEmpty from "lodash/isEmpty"
import { scroller } from "react-scroll"
import { PRODUCTS_PER_PAGE } from "@/constants/index"
import Filter from "@/components/ProductList/v2/Filter"
import Pagination from "@/components/ProductList/v2/ProductListView/Pagination"
import StickyNavHeader from "@/components/ProductList/v2/ProductListView/StickyNav/stickyNavHeader"
import PartsViewer from "@/components/PartsViewer/v1/PartsViewer"
import PartsViewerV2 from "@/components/PartsViewer/v2/PartsViewer"
import usePrevious from "@/hooks/usePrevious"
import {
  getAllFilterQueries as getFacetsFromUrl,
  getPriceSortQuery,
  getSortItemBasedOnURL,
  replaceSearchUrl,
  retainSearchQueryParams,
  organizeData,
  getUserPersona,
  shouldShowSortElement,
  getSwatchCountSearch,
} from "@/utils/helper"
import {
  getFilterQuery,
  getFlData,
  getSortList,
  handleKeyPress,
  scrollToFirstProduct,
  scrollToProductV2,
} from "@/components/Search/v1/ProductsTab/helper"
import TabLoader from "@/components/Search/v1/TabLoader"
import {
  selectProductListState,
  getProducts,
} from "@/store/features/productListSlice"
import { selectAuthState } from "@/store/features/authSlice"
import {
  searchState,
  setSearchFilter,
  setSearchTabCount,
} from "@/store/features/searchSlice"
import styles from "@/components/ProductList/v2/ProductListView/index.module.scss"
import sortStyle from "@/components/ProductList/v2/Sort/index.module.scss"
import filterStyle from "@/components/ProductList/v2/Filter/index.module.scss"
import productTileStyles from "@/components/ProductList/v2/ProductTile/index.module.scss"
import { selectGenericState } from "@/store/features/genericSlice"
import { getTranslations } from "@/components/MyFavorites/v1/MyList/localization"
import { useTranslation } from "next-i18next"
import ProductTileUI from "@/components/Shared/ProductList/v2/ProductTile/V2/ProductTileUI"
import PromoBannerV2 from "@/components/PromoBanner/v3/PromoBanner"

const ProductsTab = ({
  staticTexts = {},
  totalSearchResults = 0,
  count = 0,
  searchKeyword = "",
  onSearch = () => {},
  type = "",
  page = "",
  currencySign = "",
  lwAppName = "",
  query: queryProp = "",
  isLandscape = false,
  componentProps: { disableBestSellers = false, isAnnsacksPlp = "" } = {},
  marketParamValues = [],
  isSterling = false,
  sterlingData = [],
  brand = "",
  forceTabLoader = false,
  brandSwitcherItems = [],
  quickViewAuthData = {},
}) => {
  const dispatch = useDispatch()
  const {
    searchDetails = {},
    searchResponse: { data: { fusion: searchFusion = {} } = {} } = {},
  } = useSelector(searchState)
  const {
    data = [],
    total,
    facets = [],
    facetLabels,
    status,
    colors,
    fusion = {},
  } = useSelector(selectProductListState).plp

  const retainedFilters =
    searchDetails.keyword === searchKeyword && searchDetails[type]?.filters
      ? searchDetails[type].filters
      : []
  const { access_token: accessToken } = useSelector(selectAuthState)
  const productsPerPage = PRODUCTS_PER_PAGE
  const [width] = typeof window !== "undefined" ? [window.innerWidth] : [1024]
  const initialPartData = {
    productId: "",
    partId: "",
    isShowModal: false,
  }
  const [showFilter, setShowFilter] = useState(width >= 1024 ? true : false)
  const [rowCols, setRowCols] = useState(width < 1024 ? 2 : 3)
  const [sessionId, setSessionId] = useState("")
  const [paginationState, setPaginationState] = useState({
    curRows: parseInt(sessionStorage?.plpCurrentRows) || 30,
    start: 0,
  })
  const { curRows, start } = paginationState
  const prevRows = usePrevious(curRows)
  const [scrollToSku, setScrollToSku] = useState(
    sessionStorage?.plpScrollSku ?? ""
  )
  const filterModalWindow = useRef(null)
  const [selectedSort, setSelectedSort] = useState({})
  const [filterQueries, setFilterQueries] = useState(
    retainedFilters.length > 0 ? retainedFilters : []
  )
  const [swatchCount, setSwatchCount] = useState(5)
  const [xfLink, setXfLink] = useState("")
  const [partData, setPartsData] = useState(initialPartData)
  const [fusionQueryId, setFusionQueryId] = useState("")
  const [dataPopulated, setDataPopulated] = useState(false)
  const [dataInitPopulated, setDataInitPopulated] = useState(false)
  const [isFusionLoaded, setIsFusionLoaded] = useState(false)
  const [isRendering, setIsRendering] = useState(true)
  const [zeroResults, setZeroResults] = useState(false)
  const [localData, setLocalData] = useState(data)
  const [isLoadMore, setIsLoadMore] = useState(false)
  const [svgFileMap, setSvgFileMap] = useState([])
  const [scrollPosition, setScrollPosition] = useState(0)

  const searchResultDataLayerObj = {
    searchResult: totalSearchResults,
    searchTerm: searchKeyword,
    searchResultCategoryType: type?.toLowerCase(),
    searchResultCategoryTypeCount: count,
  }

  const { t } = useTranslation("common")
  const staticText = getTranslations(t)

  useEffect(() => {
    const selectedSortItem = getSortItemBasedOnURL(staticTexts, {
      name: staticText.featured,
    })
    setSelectedSort(selectedSortItem)
  }, [])

  useEffect(() => {
    const cookie = document?.cookie
    const cookieArray = cookie?.split(";")
    const sessionCookie = cookieArray.filter(item =>
      item?.includes("session#")
    )[0]
    const sessionIdString = sessionCookie?.split("|")[1]
    setSessionId(sessionIdString?.split("#")[1])
  }, [])

  useEffect(() => {
    setSwatchCount(getSwatchCountSearch(width))
  }, [width])

  // Reset filterQueries when tab type changes to trigger fresh data load
  useEffect(() => {
    const newFilters =
      searchDetails.keyword === searchKeyword && searchDetails[type]?.filters
        ? searchDetails[type].filters
        : []
    setFilterQueries(newFilters)
    setPaginationState({ curRows: 30, start: 0 })
  }, [type])

  useEffect(() => {
    // Set isRendering to false when we have data or when loading is complete
    if (status !== "loading" || data.length > 0) {
      setIsRendering(false)
    }
  }, [status, data])
  useEffect(() => {
    if (isSterling && sterlingData.length > 0) {
      setLocalData(sterlingData)
    } else {
      setLocalData(data)
    }
  }, [isSterling, data])

  useEffect(() => {
    if (dataInitPopulated && typeof total !== "undefined" && total === 0) {
      setZeroResults(true)
    } else {
      setZeroResults(false)
    }
  }, [dataInitPopulated, total])

  useEffect(() => {
    const search = window.location.search
    if (!_isEmpty(searchFusion) && accessToken && !isFusionLoaded) {
      setIsFusionLoaded(true)
      if (retainedFilters.length > 0) {
        setFilterQueries(retainedFilters)
      } else {
        const filterQuery = getFacetsFromUrl(
          { attributeName: "" }, // this arg to avoid inner condition
          searchFusion,
          marketParamValues
        )
        if (!search.includes("tab=")) {
          setFilterQueries(filterQuery)
          dispatch(setSearchFilter({ queries: filterQuery, key: type }))
        } else {
          if (search.includes(`tab=${type}`)) {
            setFilterQueries(filterQuery)
            dispatch(setSearchFilter({ queries: filterQuery, key: type }))
          }
        }
        retainSearchQueryParams(type)
      }
    }
  }, [searchFusion, accessToken])

  useEffect(() => {
    if (accessToken && filterQueries !== null && filterQueries !== undefined) {
      // will trigger from onLoadMore
      if (start) {
        generateListingUrl(isLoadMore, false)
        // Will trigger when user coming back from PDP page
      } else if (start === 0 && scrollToSku) {
        setScrollToSku("")
        generateListingUrl(false, true)
      }
      // When downgrade pagination
      else if (start === 0 && curRows && !!prevRows && prevRows > curRows) {
        generateListingUrl(false, true)
      }
      // will trigger from onRows. And rest of the dependencies
      else if (start === 0 && curRows) {
        generateListingUrl(false, false)
      }
    }
  }, [
    accessToken,
    start,
    curRows,
    selectedSort,
    filterQueries,
    isLoadMore,
    type,
  ])

  useEffect(() => {
    if (fusion?.fusionQueryId !== undefined) {
      setFusionQueryId(fusion.fusionQueryId)
    }
    if (
      fusion &&
      fusion?.experiment_fragments &&
      fusion?.experiment_fragments !== undefined
    ) {
      setXfLink(fusion?.experiment_fragments?.[0]?.url_s ?? "")
    }
  }, [fusion])

  useEffect(() => {
    // To scrollup the product list on back
    if (dataPopulated) scrollToProductV2()
  }, [dataPopulated])

  useEffect(() => {
    switch (true) {
      case width > 1023:
        setRowCols(3)
        break

      default:
        setRowCols(2)
    }
  }, [isLandscape, width])

  const tileClass = cx({
    "product-listing__tile-col": true,
    "product-listing__tile-col--full-width": !showFilter,
  })

  const filterClass = cx({
    "product-listing__filter": true,
    "product-listing__filter--hide": !showFilter,
    "product-listing__filter--modal": showFilter,
  })

  const sortList = getSortList(staticTexts)
  const sortsCopy = [...sortList]
  sortsCopy.splice(
    sortsCopy.findIndex(a => a.query === `bestSeller_l+asc`),
    1
  )
  const customizedSort = disableBestSellers ? sortsCopy : sortList
  // calculate promo position
  const col =
    width < 768
      ? {
          "one-column": "promo-col-3",
          "two-column": "promo-col-3",
          "three-column": "promo-col-3",
        }
      : width > 768 && width < 1024
      ? {
          "one-column": "promo-col-1",
          "two-column": "promo-col-3",
          "three-column": "promo-col-3",
        }
      : {
          "one-column": "promo-col-1",
          "two-column": "promo-col-2",
          "three-column": "promo-col-3",
        }

  const promoPosition = width < 767 ? 3 : width < 1024 ? 4 : 7

  const handleSort = item => {
    setPaginationState(state => ({ ...state, start: 0 }))
    setSelectedSort(item)
    replaceSearchUrl("sort", item?.query)
    replaceSearchUrl("tab", type)
    retainSearchQueryParams(type)
    scrollToFirstProduct()
  }

  const handleFilter = queries => {
    setIsLoadMore(false)

    dispatch(setSearchFilter({ queries, key: type }))
    setFilterQueries(queries)
    scrollToFirstProduct()
  }

  const generateListingUrl = (
    update,
    enableScroll = true,
    queries = filterQueries ?? []
  ) => {
    const personaTemp = getUserPersona()
    const sessionRows = sessionStorage?.plpCurrentRows
      ? parseInt(sessionStorage?.plpCurrentRows)
      : curRows
    // check this
    const callStart = sessionStorage?.plpCurrentRows ? 0 : start
    const fq = getFilterQuery(type, queryProp, queries, personaTemp)
    const fl = getFlData(personaTemp)
    const query = {
      fq,
      rows: sessionRows,
      start: isLoadMore ? callStart : 0,
    }
    if (selectedSort.name !== staticTexts.Featured) {
      if (selectedSort.query === "price+asc") {
        query.sort = `${getPriceSortQuery()}+asc`
      } else if (selectedSort.query === "price+desc") {
        query.sort = `${getPriceSortQuery()}+desc`
      } else {
        query.sort = selectedSort.query
      }
    }

    const arg = {
      q: searchKeyword,
      fl: fl.join(","),
      ...query,
      profileName:
        type === "Parts"
          ? "profile_service_parts_Search"
          : `profile_${lwAppName.toLowerCase()}_Search`,
      collections: type === "Parts" ? "service_parts" : lwAppName.toLowerCase(),
      displaytype: "search",
      update,
    }
    if (enableScroll) setDataPopulated(false)
    setDataInitPopulated(false)
    dispatch(getProducts({ ...arg }))
      .unwrap()
      .then(obj => {
        setDataInitPopulated(true)
        if (enableScroll) setDataPopulated(true)
        // Update the count in Redux searchDetails for this tab
        if (obj && obj.data && obj.data.total !== undefined) {
          dispatch(setSearchTabCount({ tabType: type, count: obj.data.total }))
        }
      })
      .catch(() => {
        setDataInitPopulated(false)
        if (enableScroll) setDataPopulated(false)
      })
  }

  const handleOnClickPartsDiagramLink = data => {
    const scrollY = window?.scrollY
    const getPartId = _get(
      data,
      "ProductResource.InteractiveSVG.resourceName_ss",
      ""
    )
    const resourceTitles = _get(
      data,
      "ProductResource.InteractiveSVG.resourceTitle_ss",
      ""
    )
    const partId = Array.isArray(getPartId) ? getPartId[0] : getPartId

    setPartsData({
      partId,
      productId: data.ProductProductNo_s,
      isShowModal: true,
    })

    const partLinks = organizeData(
      getPartId,
      resourceTitles,
      "Service Parts Diagram",
      "PART"
    )
    setSvgFileMap(partLinks)
    setScrollPosition(scrollY)
  }

  const executeScroll = () => {
    scroller.scrollTo("products-tab", {
      duration: 1500,
      delay: 0,
      smooth: "easeInOutQuart",
    })
  }

  // Will trigger when pagination changes
  const onRows = rows => {
    setPaginationState({ start: 0, curRows: rows })
  }

  // Will trigger on load more clicks
  const onLoadMore = () => {
    setIsLoadMore(true)
    setPaginationState(state => ({ ...state, start: start + curRows }))
  }

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
    document.getElementById(`kf-product-list-tile-details-0`)?.focus()
  }

  const onCloseModal = () => {
    setPartsData({
      ...initialPartData,
      isShowModal: false,
    })
    if (scrollPosition) {
      window.scrollTo({
        top: parseInt(scrollPosition, 10),
        behavior: "instant",
      })
    }
    setScrollPosition(0)
  }
  const { productId = "", partId = "", isShowModal = false } = partData
  let promoIndex = 0

  const { featureFlags = {} } = useSelector(selectGenericState)
  const { ENABLE_PARTS_VIEWER_V2: isPartsViewerV2Enabled = false } =
    featureFlags

  return (
    <div
      className={`${styles.productListWrapper} ${productTileStyles.productTileWrapper} ${sortStyle.productListingSearchWrapper} ${filterStyle.productListingFilterWrapper}`}
    >
      <div className="product-listing">
        <StickyNavHeader
          showFilter={showFilter}
          rowCols={rowCols}
          setShowFilter={setShowFilter}
          setRowCols={setRowCols}
          staticTexts={staticTexts}
          sorts={customizedSort}
          selectedSort={selectedSort}
          handleSort={handleSort}
          totalResults={totalSearchResults}
          searchKeyword={searchKeyword}
          onSearch={onSearch}
          type={type}
          page={page}
          isSearchTab={true}
          brand={brand}
          searchDetails={searchDetails}
        />

        {/* product-list */}
        {!isRendering && !forceTabLoader ? (
          <div className="container kf-react-plp-container product-listing__tiles">
            <div className="product-listing__row d-block">
              <div className="filter-col">
                {
                  <div
                    className={filterClass}
                    aria-modal
                    ref={filterModalWindow}
                  >
                    <div
                      className={`product-listing__filter--sticky`}
                      aria-hidden={!showFilter}
                      onKeyDown={e =>
                        handleKeyPress(
                          e,
                          width,
                          filterModalWindow,
                          setShowFilter
                        )
                      }
                    >
                      {facets && (
                        <Filter
                          tabIndex="0"
                          page={page}
                          texts={staticTexts}
                          currencySign={currencySign}
                          facetData={facets}
                          facetLabels={facetLabels}
                          currFilters={filterQueries}
                          sort={customizedSort}
                          selectedSort={selectedSort}
                          onSortChange={handleSort}
                          selectedFilterCount={
                            filterQueries &&
                            filterQueries.reduce((acc, item) => {
                              return acc + item.value.length
                            }, 0)
                          }
                          onFilter={handleFilter}
                          close={setShowFilter}
                          colors={colors}
                          displayShare={true}
                          displayPrint={true}
                          handleScroll={executeScroll}
                          showFilter={showFilter}
                          totalResults={total}
                          showSort={shouldShowSortElement(page)}
                          type={type || "products"}
                          isAnnsacksPlp={isAnnsacksPlp}
                          addToUrl
                        />
                      )}
                    </div>
                  </div>
                }
                <div className={tileClass}>
                  {localData.length === 0 ? (
                    count === 0 || zeroResults ? (
                      <div
                        className="product-list__no-results"
                        dangerouslySetInnerHTML={{
                          __html: staticTexts.searchNoResultErrorMessage,
                        }}
                      ></div>
                    ) : null
                  ) : (
                    <>
                      <div
                        className={`product-listing__row row-cols-${rowCols}`}
                      >
                        {localData.map((product, i) => {
                          return (
                            <React.Fragment
                              key={`${i}-${product.Product_Category}-${product.ctId_s}`}
                            >
                              <div
                                className="product-listing__tile"
                                key={`${i}-${product.Product_Category}-${product.ctId_s}`}
                                id={"K-" + product["sku_s"]}
                              >
                                <LazyLoad className="kf-lazyload-wrapper">
                                  <ProductTileUI
                                    data={product}
                                    highlightText={searchKeyword}
                                    index={i}
                                    swatchCount={swatchCount}
                                    handleOnClickPartsDiagramLink={
                                      handleOnClickPartsDiagramLink
                                    }
                                    parent="search"
                                    isSterling={isSterling}
                                    searchResultDataLayerObj={
                                      searchResultDataLayerObj
                                    }
                                    fusionQueryId={fusionQueryId}
                                    sessionId={sessionId}
                                    brandSwitcherItems={brandSwitcherItems}
                                    type={type}
                                    paginationState={paginationState}
                                    quickViewAuthData={quickViewAuthData}
                                  />
                                </LazyLoad>
                              </div>
                              {xfLink && i === promoPosition && (
                                <div
                                  className={`${col["one-column"]} product-listing__tile`}
                                >
                                  <PromoBannerV2
                                    xfLink={xfLink}
                                    promoIndex={++promoIndex}
                                    desktopPromo={
                                      fusion?.experiment_fragments?.[0]
                                        ?.promoLayout_s
                                    }
                                  />
                                </div>
                              )}
                            </React.Fragment>
                          )
                        })}
                      </div>
                      <Pagination
                        displayShare={true}
                        displayPrint={true}
                        totalResults={isSterling ? 1 : total}
                        isSearchTab={true}
                        {...{
                          staticTexts,
                          onLoadMore,
                          curRows,
                          data,
                          productsPerPage,
                          onRows,
                          handleBackToTop,
                          width,
                          page,
                          type,
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <TabLoader />
        )}
      </div>
      {isPartsViewerV2Enabled ? (
        <PartsViewerV2
          productId={productId}
          partId={partId}
          showModal={isShowModal}
          page={page + " results"}
          onClose={onCloseModal}
          fileMap={svgFileMap}
        />
      ) : (
        <PartsViewer
          productId={productId}
          partId={partId}
          showModal={isShowModal}
          page={page + " results"}
          onClose={onCloseModal}
        />
      )}
    </div>
  )
}

export default ProductsTab
